import { motion } from 'framer-motion';
import { Phone, MapPin, Lightning } from '@phosphor-icons/react';

const LOGO_URL = "/images/logo.jpg";

export const Hero = () => {
  return (
    <section 
      id="accueil" 
      className="relative min-h-screen flex items-center bg-[#FAFAFA] overflow-hidden pt-20"
      data-testid="hero-section"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-red-50/50 to-transparent" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-100/30 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left content */}
          <div className="space-y-6">
            {/* Logo - Grand et visible */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-2"
            >
              <img
                src={LOGO_URL}
                alt="Électricité Béchameil"
                className="h-20 sm:h-28 md:h-36 lg:h-40 w-auto"
                data-testid="hero-logo"
              />
            </motion.div>

            {/* Main title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-[#0A0A0A]"
              data-testid="hero-title"
            >
              Votre électricien
              <br />
              <span className="text-[#D32F2F]">de confiance</span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-sm sm:text-base md:text-lg text-[#4A4A4A] max-w-xl leading-relaxed"
              data-testid="hero-subtitle"
            >
              Électricité générale, domotique, bornes de recharge, alarmes et automatismes. 
              <strong className="text-[#0A0A0A]"> Plus de 20 ans d'expertise</strong> au service des particuliers et professionnels.
            </motion.p>

            {/* Location */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="flex items-center gap-2 text-[#4A4A4A]"
              data-testid="hero-location"
            >
              <MapPin weight="fill" className="w-5 h-5 text-[#D32F2F]" />
              <span className="text-sm md:text-base">Bourg-en-Bresse, Simandre-sur-Suran et alentours</span>
            </motion.div>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4 pt-2"
            >
              <a
                href="tel:0629676879"
                className="inline-flex items-center justify-center gap-2 sm:gap-3 bg-[#D32F2F] text-white rounded-full px-6 sm:px-8 py-3 sm:py-4 font-bold text-base sm:text-lg hover:bg-[#B71C1C] transition-colors duration-200"
                data-testid="hero-cta-primary"
              >
                <Phone weight="fill" className="w-5 h-5" />
                <span>06 29 67 68 79</span>
              </a>
              <a
                href="#services"
                className="inline-flex items-center justify-center gap-2 bg-[#0A0A0A] text-white rounded-full px-6 sm:px-8 py-3 sm:py-4 font-bold text-base sm:text-lg hover:bg-gray-800 transition-colors duration-200"
                data-testid="hero-cta-secondary"
              >
                Nos prestations
              </a>
            </motion.div>

            {/* Trust indicators */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="flex flex-wrap items-center gap-3 sm:gap-4 pt-4"
            >
              <div className="flex items-center gap-2" data-testid="hero-rating">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <span className="text-base sm:text-lg font-bold text-[#0A0A0A]">4,7</span>
              </div>
              <div className="h-4 sm:h-5 w-px bg-black/20" />
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="text-xs sm:text-sm text-[#4A4A4A]">14 avis Google</span>
              </div>
            </motion.div>
          </div>

          {/* Right content - Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="relative hidden lg:block"
          >
            <div className="relative">
              {/* Main image container */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/images/hero-tableau.jpg"
                  alt="Installation tableau électrique"
                  className="w-full h-[500px] object-cover"
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </div>

              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl border border-black/5" data-testid="hero-floating-card">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <Lightning weight="fill" className="w-6 h-6 text-[#D32F2F]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#0A0A0A]">+20 ans</p>
                    <p className="text-sm text-[#4A4A4A]">d'expérience</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
